// [Codex 2026-07-12] S135 uses the canonical black glass plate for the selected mobile destination.
// [Codex 2026-07-04] S99 mobile footer keeps Forum wired to the first-class mobile voice floor.
import { MessageCircle, Newspaper, Stadium, Users } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import type { SurfaceNavTab } from "../../lib/surface-capabilities";
import { ArbitrumGlyph } from "../icons/ArbitrumGlyph";

type MobileConsiliumView = "chat" | "forum" | "arbitrum";

interface MobileQuickFooterProps {
  activeTab: SurfaceNavTab;
  activeConsiliumView: MobileConsiliumView;
  style?: CSSProperties;
  onTabChange: (tab: SurfaceNavTab) => void;
  onConsiliumView: (view: MobileConsiliumView) => void;
}

export function MobileQuickFooter({
  activeTab,
  activeConsiliumView,
  style,
  onTabChange,
  onConsiliumView,
}: MobileQuickFooterProps) {
  const activeIndex = getQuickFooterActiveIndex(activeTab, activeConsiliumView);
  const forumActive =
    activeTab === "analysis" && activeConsiliumView === "forum";
  const footerStyle: CSSProperties = {
    ...style,
    height: "calc(env(safe-area-inset-bottom, 0px) + 78px)",
    paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
  };

  return (
    <footer
      className="pointer-events-none relative z-10 shrink-0 bg-black px-4 pt-2 md:hidden"
      data-mobile-quick-footer="true"
      style={footerStyle}
    >
      <nav
        className="pointer-events-auto relative mx-auto grid h-[50px] max-w-[360px] grid-cols-5 items-center overflow-visible bg-transparent px-2"
        aria-label="Mobile quick access"
        data-active-index={activeIndex}
        style={
          {
            "--mobile-quick-active-index": activeIndex,
          } as CSSProperties
        }
      >
        <span
          className="mobile-quick-footer-blob fintheon-black-liquid-glass solvys-liquid-glass"
          aria-hidden="true"
        />
        <FooterButton
          label="Desk"
          active={activeTab === "dashboard"}
          icon={<Users className="h-[13px] w-[13px]" />}
          onClick={() => onTabChange("dashboard")}
          dataTourTarget="dashboard"
        />
        <FooterButton
          label="RiskFlow"
          active={activeTab === "riskflow"}
          icon={<Newspaper className="h-[13px] w-[13px]" />}
          onClick={() => onTabChange("riskflow")}
          dataTourTarget="riskflow"
        />
        <FooterButton
          label="Chat"
          active={activeTab === "analysis" && activeConsiliumView === "chat"}
          icon={<MessageCircle className="h-[13px] w-[13px]" />}
          onClick={() => onConsiliumView("chat")}
          dataTourTarget="analysis"
        />
        <FooterButton
          label="Forum"
          active={forumActive}
          icon={<Stadium className="h-[13px] w-[13px]" />}
          onClick={() => onConsiliumView("forum")}
        />
        <FooterButton
          label="Arbitrum"
          active={
            activeTab === "analysis" && activeConsiliumView === "arbitrum"
          }
          icon={<ArbitrumGlyph size={13} />}
          onClick={() => onConsiliumView("arbitrum")}
        />
      </nav>
    </footer>
  );
}

function getQuickFooterActiveIndex(
  activeTab: SurfaceNavTab,
  activeConsiliumView: MobileConsiliumView,
) {
  if (activeTab === "riskflow") return 1;
  if (activeTab === "analysis") {
    if (activeConsiliumView === "chat") return 2;
    if (activeConsiliumView === "forum") return 3;
    if (activeConsiliumView === "arbitrum") return 4;
  }
  return 0;
}

function FooterButton({
  label,
  active,
  icon,
  onClick,
  dataTourTarget,
}: {
  label: string;
  active: boolean;
  icon: ReactNode;
  onClick: () => void;
  dataTourTarget?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      data-tour-target={dataTourTarget}
      title={label}
      className={`mobile-quick-footer-button relative z-[1] flex h-full min-w-0 flex-col items-center justify-center gap-px rounded-[19px] transition-[color,opacity,text-shadow] duration-200 active:scale-[0.98] ${
        active
          ? "text-[var(--fintheon-accent)] [text-shadow:0_0_14px_rgba(199,159,74,0.2)]"
          : "text-[var(--fintheon-text)]/48 hover:text-[var(--fintheon-text)]/72"
      }`}
    >
      <span className="mobile-quick-footer-button__icon inline-flex h-[23px] w-[33px] items-center justify-center rounded-full transition-colors duration-200">
        {icon}
      </span>
      <span className="mobile-quick-footer-button__label max-w-full truncate text-[8px] font-medium uppercase leading-none tracking-[0.08em]">
        {label}
      </span>
    </button>
  );
}
